import { useCallback, useEffect, useState } from 'react';
import type { ActivityFilters, ActivityFilterOptions } from '../../api/types';

function invalidFilterPatch(
  filters: ActivityFilters,
  options: ActivityFilterOptions,
) {
  if (
    filters.azureProject &&
    !options.projects.includes(filters.azureProject)
  ) {
    return { azureProject: undefined, azureRepository: undefined, page: 1 };
  }
  if (
    filters.azureRepository &&
    !options.repositories.includes(filters.azureRepository)
  ) {
    return { azureRepository: undefined, page: 1 };
  }
  if (filters.createdBy && !options.creators.includes(filters.createdBy)) {
    return { createdBy: undefined, page: 1 };
  }
  return undefined;
}

export function usePortfolioFilters() {
  const [filters, setFilters] = useState<ActivityFilters>({
    page: 1,
    limit: 20,
    mineOnly: true,
  });
  const change = useCallback((patch: Partial<ActivityFilters>) => {
    setFilters(prev => ({ ...prev, ...patch, page: 1 }));
  }, []);
  const reset = useCallback(() => {
    setFilters(prev => ({
      page: 1,
      limit: prev.limit ?? 20,
      mineOnly: prev.mineOnly ?? true,
    }));
  }, []);
  const changePage = (page: number) =>
    setFilters(prev => ({ ...prev, page: page + 1 }));
  return { filters, setFilters, change, reset, changePage };
}

export function usePortfolioFilterReconciliation(
  { filters, setFilters }: ReturnType<typeof usePortfolioFilters>,
  options: ActivityFilterOptions,
  ready: boolean,
) {
  const { azureProject, azureRepository, createdBy } = filters;
  useEffect(() => {
    // Retained options after a failed query must never invalidate selection.
    if (!ready) return;
    const patch = invalidFilterPatch(
      { azureProject, azureRepository, createdBy },
      options,
    );
    if (patch) setFilters(prev => ({ ...prev, ...patch }));
  }, [ready, options, azureProject, azureRepository, createdBy, setFilters]);
}
export type PortfolioFilters = ReturnType<typeof usePortfolioFilters>;
